/**
 * Client-side credential helpers for Cortex AI providers.
 *
 * These functions are framework-agnostic and safe to import from browser bundles.
 * They do not store keys; they only validate format and, when a key is provided,
 * ask the brain edge function to confirm the key is accepted by the provider.
 */

export interface CheckCredentialsOptions {
  /** Supabase project URL (e.g. https://xxx.supabase.co) */
  supabaseUrl: string;
  /** Current user's access token (JWT) */
  accessToken: string;
  /** Provider to validate */
  provider: 'gemini' | 'openrouter';
  /** Candidate API key (optional for OAuth checks; required for BYOK) */
  key?: string;
  /** Abort signal for timeouts */
  signal?: AbortSignal;
}

export interface CheckCredentialsResult {
  ok: boolean;
  /** Provider-normalized model id returned when available */
  model?: string;
  /** User-facing error if not ok */
  error?: string;
  /** Machine-readable error code */
  code?: string;
}

const GEMINI_KEY_PREFIX = 'AIza';
const OPENROUTER_KEY_PREFIX = 'sk-or-v1-';
const MIN_GEMINI_KEY_LENGTH = 20;
const MIN_OPENROUTER_KEY_LENGTH = 40;

/** True if the string looks like a Google AI Studio / Gemini API key. */
export function isGeminiKeyFormat(key: string): boolean {
  if (!key || typeof key !== 'string') return false;
  const trimmed = key.trim();
  return trimmed.startsWith(GEMINI_KEY_PREFIX) && trimmed.length >= MIN_GEMINI_KEY_LENGTH;
}

/** True if the string looks like an OpenRouter API key. */
export function isOpenRouterKeyFormat(key: string): boolean {
  if (!key || typeof key !== 'string') return false;
  const trimmed = key.trim();
  return trimmed.startsWith(OPENROUTER_KEY_PREFIX) && trimmed.length >= MIN_OPENROUTER_KEY_LENGTH;
}

/**
 * Format-validate a key without making network calls.
 * Returns a user-facing error string or undefined.
 */
export function validateKeyFormat(
  provider: 'gemini' | 'openrouter',
  key: string,
): { valid: boolean; error?: string } {
  const trimmed = key.trim();
  if (!trimmed) {
    return { valid: false, error: 'API key is required.' };
  }

  if (provider === 'gemini') {
    if (!isGeminiKeyFormat(trimmed)) {
      return {
        valid: false,
        error: `Gemini keys start with "${GEMINI_KEY_PREFIX}" and are at least ${MIN_GEMINI_KEY_LENGTH} characters.`,
      };
    }
    return { valid: true };
  }

  if (provider === 'openrouter') {
    if (!isOpenRouterKeyFormat(trimmed)) {
      return {
        valid: false,
        error: `OpenRouter keys start with "${OPENROUTER_KEY_PREFIX}" and are at least ${MIN_OPENROUTER_KEY_LENGTH} characters.`,
      };
    }
    return { valid: true };
  }

  return { valid: false, error: 'Unsupported provider.' };
}

/**
 * Ask the brain edge function to confirm a key is accepted by the upstream provider.
 *
 * The brain performs a cheap, non-inference check (model list / auth lookup) so the
 * user is not billed for validation.
 */
export async function checkCredentials(
  options: CheckCredentialsOptions,
): Promise<CheckCredentialsResult> {
  const { supabaseUrl, accessToken, provider, key, signal } = options;

  if (!supabaseUrl) {
    return { ok: false, error: 'Supabase URL is not configured.', code: 'missing_config' };
  }

  if (!accessToken) {
    return { ok: false, error: 'You must be signed in to validate AI credentials.', code: 'unauthorized' };
  }

  if (key) {
    const format = validateKeyFormat(provider, key);
    if (!format.valid) {
      return { ok: false, error: format.error, code: 'invalid_format' };
    }
  }

  const url = new URL(`${supabaseUrl}/functions/v1/brain/check-credentials`);

  try {
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ provider, key }),
      signal,
    });

    const body = (await response.json().catch(() => ({}))) as {
      ok?: boolean;
      model?: string;
      error?: string;
      code?: string;
    };

    if (response.ok && body.ok) {
      return { ok: true, model: body.model };
    }

    return {
      ok: false,
      error: body.error || `Credential check failed (${response.status}).`,
      code: body.code || 'check_failed',
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (message.includes('abort')) {
      return { ok: false, error: 'Credential check timed out.', code: 'timeout' };
    }
    return { ok: false, error: `Network error: ${message}`, code: 'network_error' };
  }
}
